import * as Sentry from '@sentry/browser';

import type { SentryClientInstance, SentryOptions } from '../declarations/Clients';
import { isSentryEnabledEnv } from '../sentry-environment';
import { getDeployEnv } from './deployEnv';
import { buildDefaultSentryOpts } from './sentry';

type ProcessEnv = Record<string, string | undefined>;

declare const process: { env: ProcessEnv };

type ImportEnv = {
  readonly ODFE_GIT_SHA?: string;
  readonly REACT_APP_GIT_SHA?: string;
};

/**
 * Initializes the bundled Sentry SDK and returns the subset of it that
 * `SentryClientInstance` promises.
 *
 * The SDK has no single client object exposing these four methods — they are module-level
 * functions that resolve the active scope internally — so this adapts the namespace to the
 * structural contract the rest of the package consumes.
 */
export const defaultInitSentry = (sentryOptions: SentryOptions): SentryClientInstance => {
  if (isSentryEnabledEnv(sentryOptions.environment)) {
    Sentry.init(sentryOptions as Sentry.BrowserOptions);
  }
  return {
    addBreadcrumb: (breadcrumb: Sentry.Breadcrumb) => {
      Sentry.addBreadcrumb(breadcrumb);
    },
    captureException: (exception: unknown, hint?: Sentry.EventHint) =>
      Sentry.captureException(exception, hint),
    captureMessage: (message: string, hint?: Sentry.CaptureContext | Sentry.SeverityLevel) =>
      Sentry.captureMessage(message, hint),
    withScope: (callback: (scope: Sentry.Scope) => void) => {
      Sentry.withScope(callback);
    },
  };
};

const getGitSha = (): string => {
  let importSha: string | undefined;
  try {
    const importEnv = (import.meta as { env?: ImportEnv }).env;
    importSha = importEnv?.ODFE_GIT_SHA ?? importEnv?.REACT_APP_GIT_SHA;
  } catch {
    // import.meta not available in this runtime
  }

  let odfeProcessSha: string | undefined;
  try {
    odfeProcessSha = process.env.ODFE_GIT_SHA;
  } catch {
    // process is not available or this expression was not replaced by the bundler
  }

  let reactAppProcessSha: string | undefined;
  try {
    reactAppProcessSha = process.env.REACT_APP_GIT_SHA;
  } catch {
    // process is not available or this expression was not replaced by the bundler
  }

  return importSha ?? odfeProcessSha ?? reactAppProcessSha ?? 'unknown';
};

/**
 * TODO:
 * This is kept for backwards compatibility. We should let observability lib handle Sentry initialization,
 * which might require us to build a version of observability for non-consumer apps.
 */
export const loadSentry = (url: string, sentryOptions: Sentry.BrowserOptions = {}) => {
  const env = getDeployEnv();
  defaultInitSentry({
    ...buildDefaultSentryOpts({ appName: '', env, version: getGitSha() }),
    ...sentryOptions,
    dsn: url,
  } as SentryOptions);
};

// Export Sentry directly here so that existing behavior is maintained
// eventually we want to use the "globalObservability.getSentryClient()" proxy
// instance here
export { Sentry };
